/**
 * SettingsScreen — App configuration.
 *
 * Settings v4: iOS-style grouped cards. Content area is --rc-surface2 so
 * --rc-paper cards read clearly. Values are right-aligned in the same row.
 * 5 groups: Account · Sound · During Retreat · Practice · App.
 */
import { MetaStrip } from "../components/MetaStrip";
import styles from "./SettingsScreen.module.css";
import { TEST_IDS } from "../testing/testIds";
import type { PreferredTradition } from "../types/ui";

type Props = {
  preferredTradition: PreferredTradition;
  onSetTradition: (value: PreferredTradition) => void;
  bellsEnabled: boolean;
  onToggleBells: (value: boolean) => void;
  currentBellName: string;
  onOpenBellLibrary: () => void;
  ttsEnabled: boolean;
  onToggleTTS: (value: boolean) => void;
  whispersEnabled: boolean;
  onToggleWhispers: (value: boolean) => void;
  audioReflectionEnabled: boolean;
  onToggleAudioReflection: (value: boolean) => void;
  appVersion: string;
  isRetreatActive: boolean;
  isAdmin: boolean;
  userEmail?: string;
  onSignOut: (() => void) | undefined;
  onOpenTeaching: () => void;
  onOpenFeedback: () => void;
  onOpenAdmin: () => void;
  onOpenWhatsNew: () => void;
  updatesUnreadCount: number;
  onBack: () => void;
};

// ── Toggle ────────────────────────────────────────────────────────────────────

function Toggle({ checked, onChange, label }: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      className={`${styles.toggle} ${checked ? styles.toggleOn : ""}`}
      onClick={() => onChange(!checked)}
      aria-label={label}
      type="button"
    >
      <div className={styles.toggleThumb} />
    </button>
  );
}

// ── Row primitives ────────────────────────────────────────────────────────────

function ToggleRow({ title, value, onChange, sub }: {
  title: string;
  value: boolean;
  onChange: (v: boolean) => void;
  sub?: boolean;
}) {
  return (
    <div className={`${styles.row} ${styles.rowToggle} ${sub ? styles.rowSub : ""}`}>
      <span className={styles.rowTitle}>{title}</span>
      <Toggle checked={value} onChange={onChange} label={title} />
    </div>
  );
}

function LinkRow({ title, description, value, onClick, testId, badge, danger, accent, sub }: {
  title: string;
  description?: string;
  value?: string;
  onClick: () => void;
  testId?: string;
  badge?: boolean;
  danger?: boolean;
  accent?: boolean;
  sub?: boolean;
}) {
  return (
    <button
      className={[
        styles.row,
        danger ? styles.rowDanger : "",
        accent ? styles.rowAccent : "",
        sub ? styles.rowSub : "",
      ].filter(Boolean).join(" ")}
      onClick={onClick}
      type="button"
      data-testid={testId}
    >
      <div className={styles.rowTitleGroup}>
        <div>
          <span className={styles.rowTitle}>{title}</span>
          {description && <div className={styles.rowDesc}>{description}</div>}
        </div>
        {badge && <span className={styles.unreadDot} />}
      </div>
      <div className={styles.rowRight}>
        {value && <span className={styles.rowValue}>{value}</span>}
        <span className={styles.rowChevron}>›</span>
      </div>
    </button>
  );
}

function InfoRow({ value }: { value: string }) {
  return (
    <div className={`${styles.row} ${styles.rowInfo}`}>
      <span className={styles.rowInfoValue}>{value}</span>
    </div>
  );
}

function SignOutRow({ onClick }: { onClick: () => void }) {
  return (
    <button
      className={`${styles.row} ${styles.rowDanger}`}
      onClick={onClick}
      type="button"
      data-testid={TEST_IDS.SETTINGS_SIGN_OUT_BTN}
    >
      <span className={styles.rowTitle}>Sign out</span>
    </button>
  );
}

function TraditionRow({ value, onChange }: {
  value: PreferredTradition;
  onChange: (v: PreferredTradition) => void;
}) {
  const label = value === "zen" ? "Zen" : value === "vipassana" ? "Vipassana" : "None";
  const next: PreferredTradition = value === null ? "zen" : value === "zen" ? "vipassana" : null;
  return (
    <button
      className={styles.row}
      onClick={() => onChange(next)}
      type="button"
      data-testid={TEST_IDS.SETTINGS_TRADITION_ROW}
    >
      <span className={styles.rowTitle}>Preferred tradition</span>
      <div className={styles.rowRight}>
        <span className={styles.rowValue}>{label}</span>
        <span className={styles.rowChevron}>›</span>
      </div>
    </button>
  );
}

// ── Section card ──────────────────────────────────────────────────────────────

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section className={styles.section}>
      <p className={styles.sectionLabel}>{label}</p>
      <div className={styles.card}>{children}</div>
    </section>
  );
}

// ── Screen ────────────────────────────────────────────────────────────────────

export function SettingsScreen({
  preferredTradition, onSetTradition,
  bellsEnabled, onToggleBells,
  currentBellName, onOpenBellLibrary,
  ttsEnabled, onToggleTTS,
  whispersEnabled, onToggleWhispers,
  audioReflectionEnabled, onToggleAudioReflection,
  appVersion, isRetreatActive, isAdmin,
  userEmail, onSignOut,
  onOpenTeaching, onOpenFeedback, onOpenAdmin,
  onOpenWhatsNew, updatesUnreadCount,
  onBack,
}: Props) {
  return (
    <div className={styles.screen} data-testid={TEST_IDS.SETTINGS_SCREEN}>
      <MetaStrip label="Settings" onBack={onBack} />

      <div className={styles.content}>

        {isRetreatActive && (
          <p className={styles.retreatNote}>
            Some settings are unavailable during retreat.
          </p>
        )}

        {/* Account */}
        {(userEmail || onSignOut) && (
          <Section label="Account">
            {userEmail && <InfoRow value={userEmail} />}
            {onSignOut && <SignOutRow onClick={onSignOut} />}
          </Section>
        )}

        {/* App — feedback first, right below Account */}
        <Section label="App">
          <LinkRow
            title="Send feedback"
            description="Help shape the retreat experience"
            onClick={onOpenFeedback}
            accent
          />
          <LinkRow
            title="What's new"
            onClick={onOpenWhatsNew}
            testId={TEST_IDS.SETTINGS_WHATS_NEW_ROW}
            badge={updatesUnreadCount > 0}
          />
          <LinkRow title="Bell meanings" onClick={onOpenTeaching} />
          {isAdmin && (
            <LinkRow title="Admin" value="Wave stats · inbox" onClick={onOpenAdmin} />
          )}
        </Section>

        {/* Sound — hidden entirely during retreat except voice guidance */}
        <Section label="Sound">
          {!isRetreatActive && (
            <>
              <ToggleRow title="Bells" value={bellsEnabled} onChange={onToggleBells} />
              {bellsEnabled && (
                <LinkRow
                  title="Bell style"
                  value={currentBellName}
                  onClick={onOpenBellLibrary}
                  testId={TEST_IDS.SETTINGS_BELL_STYLE_ROW}
                  sub
                />
              )}
            </>
          )}
          <ToggleRow title="Voice guidance" value={ttsEnabled} onChange={onToggleTTS} />
        </Section>

        {/* During retreat */}
        <Section label="During retreat">
          <ToggleRow title="Whispers" value={whispersEnabled} onChange={onToggleWhispers} />
          <ToggleRow title="Voice recording" value={audioReflectionEnabled} onChange={onToggleAudioReflection} />
        </Section>

        {/* Practice */}
        {!isRetreatActive && (
          <Section label="Practice">
            <TraditionRow value={preferredTradition} onChange={onSetTradition} />
          </Section>
        )}

        <p className={styles.footer}>
          Retreat Conductor <span className={styles.versionNum}>{appVersion}</span>
        </p>
      </div>
    </div>
  );
}
